
import {
  computed, defineComponent, inject, onUnmounted, PropType, ref, Ref,
} from 'vue';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import SharedPage from './SharedPage.vue';
import {
  ICase,
  IFolder,
  IGroup,
  IMedia,
  IRequestSpacesContact,
} from '@/types/interfaces';
import {
  CASE_SHARE_POPUP_STATES, SHARE_ACCESS, SHARE_TYPES,
} from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import ShareMenu from './ShareMenu.vue';
import ShareByNetwork from './ShareByNetwork.vue';
import ShareByMessage from './ShareByMessage.vue';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import DriveMenuPopUp from '@/modules/drive/components/UI/DriveMenuPopUp.vue';

export default defineComponent({
  components: {
    AppBottomPopUp,
    AppLoader,
    SharedPage,
    ShareMenu,
    ShareByNetwork,
    ShareByMessage,
    DriveMenuPopUp,
  },
  props: {
    item: { type: Object as PropType<IMedia | IFolder | ICase>, required: true },
    itemType: { type: Number as PropType<SHARE_TYPES>, required: true },
    preloadFolderSharedInfo: { type: Boolean, default: false },
    positionData: { type: Object },
  },
  // emits: ['hide', 'setItemShared', 'setSharedLabels', 'onShareByMessage'],
  emits: ['hide', 'onShareByMessage'],
  setup(props, { emit }) {
    const spacesStore = useSpacesStore();
    const mainStore = useMainStore();
    const stateController: Ref<CASE_SHARE_POPUP_STATES> = ref(CASE_SHARE_POPUP_STATES.MENU);
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;

    const selectedGroupsId: Ref<string[]> = ref([]);
    const selectedContactsId: Ref<string[]> = ref([]);
    const sharing: Ref<boolean> = ref(false);

    const myContacts: Ref<IRequestSpacesContact[]> = ref([...spacesStore.myContacts]);
    const myGroups: Ref<IGroup[]> = ref([...spacesStore.myGroups]);

    const allMyContacts: Ref<boolean> = ref(false);

    const sharedPage: Ref<SHARE_ACCESS | null> = ref(null);

    const selectedAll = computed(
      () => (selectedContactsId.value.length === myContacts.value.length) && (selectedGroupsId.value.length === myGroups.value.length),
    );

    const deselectedAll = computed(() => selectedGroupsId.value.length + selectedContactsId.value.length === 0);

    const contactList = computed(() => (allMyContacts.value ? myContacts.value : myContacts.value.slice(0, 3)));

    const allMyGroups: Ref<boolean> = ref(false);

    const groupList = computed(() => (allMyGroups.value ? myGroups.value : myGroups.value.slice(0, 3)));

    const shareTitle = computed(() => {
      if (props.itemType === SHARE_TYPES.CASE) {
        return $localize('share.photo.popup.media.type.case');
      } if (props.itemType === SHARE_TYPES.FOLDER) {
        return $localize('share.photo.popup.media.type.folder');
      } if (props.itemType === SHARE_TYPES.FILE) {
        return $localize('share.photo.popup.media.type.file');
      }
      return $localize('share.photo.popup.media.type.photo');
    });

    function switchState(state: CASE_SHARE_POPUP_STATES) {
      stateController.value = state;
    }

    function hidePopUp() {
      emit('hide');
    }

    function onDeselectAll(): void {
      selectedGroupsId.value = [];
      selectedContactsId.value = [];
      // eslint-disable-next-line no-param-reassign
      myContacts.value.forEach((my) => { my.selected = false; });
      // eslint-disable-next-line no-param-reassign
      myGroups.value.forEach((my) => { my.selected = false; });
    }

    function onSelectAll(): void {
      if (selectedAll.value) {
        onDeselectAll();
      } else {
        selectedGroupsId.value = [];
        selectedContactsId.value = [];

        myContacts.value.forEach((my) => {
          // eslint-disable-next-line no-param-reassign
          my.selected = true;
          selectedContactsId.value.push(my.user.accountId);
        });
        myGroups.value.forEach((group) => {
          // eslint-disable-next-line no-param-reassign
          group.selected = true;
          selectedGroupsId.value.push(group.id);
        });
      }
    }

    function onShareAccess(): void {
      // is declared but it is not using now. Current code will not work correctly with new app logic

      // sharing.value = true;

      // const payload = getShareToPlayload();

      // DriveService.shareAccess(props.item.id!, payload, props.itemType)
      //   .then((response) => {
      //     if (response.success) {
      //       emit('setSharedLabels', []);

      //       payload.data.forEach((share) => {
      //         switch (share.accessType) {
      //           case SHARE_ACCESS.GROUP:
      //             emit('setSharedLabels', groupsToLables(myGroups.value, share.accessContent));
      //             break;
      //           case SHARE_ACCESS.SPECIFIC_PEOPLE:
      //             emit('setSharedLabels', contactsToLables(myContacts.value, share.accessContent));
      //             break;
      //           case SHARE_ACCESS.EVERYONE:
      //             emit('setSharedLabels', everyoneToLables(myContacts.value, myGroups.value));
      //             break;
      //           default:
      //             break;
      //         }
      //         setSharedStatusToItem();
      //       });
      //     }
      //   })
      //   // .then(() => driveStore.fetchPhotos(0)) TODO check if setSharedStatusToItem() working correct
      //   .finally(() => {
      //     sharing.value = false;
      //     hidePopUp();
      //   });
    }

    function onSelectContact(contact: IRequestSpacesContact): void {
      if (contact.selected) {
        // eslint-disable-next-line no-param-reassign
        contact.selected = false;
        selectedContactsId.value = selectedContactsId.value.filter((id) => id !== contact.user.accountId);
      } else {
        // eslint-disable-next-line no-param-reassign
        contact.selected = true;
        selectedContactsId.value.push(contact.user.accountId);
      }
    }

    function onSelectGroup(group: IGroup): void {
      if (group.selected) {
        // eslint-disable-next-line no-param-reassign
        group.selected = false;
        selectedGroupsId.value = selectedGroupsId.value.filter((id) => id !== group.id);
      } else {
        // eslint-disable-next-line no-param-reassign
        group.selected = true;
        selectedGroupsId.value.push(group.id);
      }
    }

    function onSelectFromPage(select: { data: IGroup | IRequestSpacesContact, type: SHARE_ACCESS }) {
      if (select.type === SHARE_ACCESS.GROUP) {
        onSelectGroup(select.data as IGroup);
      } else if (select.type === SHARE_ACCESS.SPECIFIC_PEOPLE) {
        onSelectContact(select.data as IRequestSpacesContact);
      }
    }

    function onShareByMessage() {
      emit('onShareByMessage');
    }

    onUnmounted(() => {
      onDeselectAll();
    });

    return {
      shareTitle,
      sharedPage,
      groupList,
      myGroups,
      myContacts,
      sharing,
      selectedGroupsId,
      selectedContactsId,
      selectedAll,
      deselectedAll,
      contactList,
      allMyContacts,
      stateController,
      hidePopUp,
      switchState,
      onSelectAll,
      onDeselectAll,
      onSelectContact,
      onSelectGroup,
      onShareAccess,
      onSelectFromPage,
      onShareByMessage,
      CASE_SHARE_POPUP_STATES,
      SHARE_ACCESS,
      SHARE_TYPES,
      mainStore,
    };
  },
});
